import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Breadcrumbs from "src/components/base/breadcrumb";
import { Heading } from "src/components/base/heading";
import { Divider } from "src/components/base/divider";
import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { ApiInternal } from "src/api/api_internal";
import Form from "src/components/base/form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { PrimaryButton } from "src/components/base/button";
import { Switch, SwitchField } from "src/components/base/switch";

/** Form to create a local group */
function RouteComponent() {
    const [t] = useTranslation("dialog-create-group");

    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {
            name: "",
            description: "",
            internet: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.groups.local.create({
                    name: value.name,
                    description: value.description,
                    internet: value.internet,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error.name ? t("error.name-already-occupied") : undefined,
                        },
                    };
                }

                await navigate({
                    to: "/a/groups/$groupId/general",
                    params: { groupId: res.value.uuid },
                    search: { search: "", page: 1 },
                });
            },
        },
    });

    return (
        <div className={"flex flex-col gap-6"}>
            <div className={"flex flex-col gap-3"}>
                <Breadcrumbs
                    items={[{ label: t("button.groups"), to: "/a/groups/local" }, { label: t("label.create") }]}
                />
                <Heading>{t("heading.create-group")}</Heading>
                <Divider />
            </div>

            <Form onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"name"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.name")}</RequiredLabel>
                                    <Input
                                        required
                                        autoComplete={"off"}
                                        autoFocus
                                        maxLength={255}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                    />
                                    {fieldApi.state.meta.errors.map((error) => (
                                        <ErrorMessage>{error}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"description"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.description")}</Label>
                                    <Input
                                        autoComplete={"off"}
                                        value={fieldApi.state.value}
                                        maxLength={1024}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"internet"}>
                            {(fieldApi) => (
                                <SwitchField>
                                    <Label>{t("label.allow-exit-nodes")}</Label>
                                    <Description>{t("description.allow-exit-nodes")}</Description>
                                    <Switch
                                        color={"blue"}
                                        checked={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e)}
                                    />
                                </SwitchField>
                            )}
                        </form.Field>

                        <div>
                            <PrimaryButton type={"submit"}>{t("button.create-group")}</PrimaryButton>
                        </div>
                    </FieldGroup>
                </Fieldset>
            </Form>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/groups/create-local")({
    component: RouteComponent,
});
